import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Lock, ShieldCheck } from 'lucide-react';

import { changeAdminPassword, ChangePasswordPayload } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

/**
 * Self-service admin password change form backed by the change-password endpoint.
 * Handles client-side validation, submission feedback, and session expiry fallback.
 */
export function ChangePasswordForm() {
  const { logout } = useAuth();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (payload: ChangePasswordPayload) => changeAdminPassword(payload),
    onSuccess: () => {
      toast.success('เปลี่ยนรหัสผ่านสำเร็จ');
      reset();
    },
    onError: (error: any) => {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        toast.error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง');
        logout();
        return;
      }

      // Handle error response properly - backend sends { error: { code, message } }
      const errorData = error?.response?.data?.error;
      const message = typeof errorData === 'string'
        ? errorData
        : errorData?.message || 'ไม่สามารถเปลี่ยนรหัสผ่านได้';
      toast.error(message);
    },
  });

  const onSubmit = handleSubmit(values => {
    if (mutation.isLoading) {
      return;
    }

    mutation.mutate({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  });

  const newPasswordValue = watch('newPassword');
  const currentPasswordValue = watch('currentPassword');
  const currentPasswordId = useId();
  const newPasswordId = useId();
  const confirmPasswordId = useId();

  const passwordRequirements = [
    'อย่างน้อย 8 ตัวอักษร',
    'ต้องมีทั้งตัวอักษรและตัวเลข',
    'ไม่ซ้ำกับรหัสผ่านปัจจุบัน',
  ];

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      <div className="space-y-6">
        <div>
          <label htmlFor={currentPasswordId} className="mb-2 block text-sm font-medium text-gray-700">
            รหัสผ่านปัจจุบัน
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id={currentPasswordId}
              type={showCurrent ? 'text' : 'password'}
              className="block w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-12 text-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="กรอกรหัสผ่านปัจจุบัน"
              {...register('currentPassword', {
                required: 'กรุณาใส่รหัสผ่านปัจจุบัน',
                minLength: { value: 8, message: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร' },
              })}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(value => !value)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              {showCurrent ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.currentPassword && (
            <p className="mt-2 text-sm text-red-600">
              {String(errors.currentPassword.message ?? '')}
            </p>
          )}
        </div>

        <div>
          <label htmlFor={newPasswordId} className="mb-2 block text-sm font-medium text-gray-700">
            รหัสผ่านใหม่
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <ShieldCheck className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id={newPasswordId}
              type={showNew ? 'text' : 'password'}
              className="block w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-12 text-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="ตั้งรหัสผ่านใหม่"
              {...register('newPassword', {
                required: 'กรุณาใส่รหัสผ่านใหม่',
                minLength: { value: 8, message: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร' },
                pattern: {
                  value: /^(?=.*[A-Za-z])(?=.*\d).{8,}$/,
                  message: 'รหัสผ่านต้องมีทั้งตัวอักษรและตัวเลข',
                },
                validate: value =>
                  value !== currentPasswordValue ||
                  'รหัสผ่านใหม่ต้องไม่ตรงกับรหัสผ่านปัจจุบัน',
              })}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowNew(value => !value)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            รหัสผ่านควรประกอบด้วยตัวอักษรพิมพ์เล็ก พิมพ์ใหญ่ และตัวเลขผสมกัน
          </p>
          {errors.newPassword && (
            <p className="mt-2 text-sm text-red-600">
              {String(errors.newPassword.message ?? '')}
            </p>
          )}
        </div>

        <div>
          <label htmlFor={confirmPasswordId} className="mb-2 block text-sm font-medium text-gray-700">
            ยืนยันรหัสผ่านใหม่
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <ShieldCheck className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id={confirmPasswordId}
              type={showConfirm ? 'text' : 'password'}
              className="block w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-12 text-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="ยืนยันรหัสผ่านใหม่อีกครั้ง"
              {...register('confirmNewPassword', {
                required: 'กรุณายืนยันรหัสผ่านใหม่',
                validate: value =>
                  value === newPasswordValue || 'รหัสผ่านใหม่ไม่ตรงกัน',
              })}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(value => !value)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.confirmNewPassword && (
            <p className="mt-2 text-sm text-red-600">
              {String(errors.confirmNewPassword.message ?? '')}
            </p>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
        <h3 className="mb-2 font-medium text-gray-800">เงื่อนไขรหัสผ่าน</h3>
        <ul className="list-inside list-disc space-y-1">
          {passwordRequirements.map(requirement => {
            const met = requirement === 'ไม่ซ้ำกับรหัสผ่านปัจจุบัน'
              ? !!newPasswordValue && newPasswordValue !== currentPasswordValue
              : requirement === 'อย่างน้อย 8 ตัวอักษร'
                ? (newPasswordValue?.length ?? 0) >= 8
                : /^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(newPasswordValue ?? '');

            return (
              <li
                key={requirement}
                className={met ? 'text-green-600' : undefined}
              >
                {requirement}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => reset()}
          className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-600 transition hover:border-gray-400 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={mutation.isLoading || isSubmitting}
        >
          ล้างข้อมูล
        </button>
        <button
          type="submit"
          className="inline-flex items-center rounded-lg bg-gradient-to-r from-yellow-400 to-orange-400 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-orange-400 hover:to-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={mutation.isLoading || isSubmitting}
        >
          {mutation.isLoading ? 'กำลังบันทึก...' : 'เปลี่ยนรหัสผ่าน'}
        </button>
      </div>
    </form>
  );
}
